export const SUBSCRIPTION_TIERS = {
  free: 'free',
  professional: 'professional',
} as const

export type SubscriptionTier = keyof typeof SUBSCRIPTION_TIERS
export type BillingInterval = 'month' | 'year'
export type SubscriptionStatus = 'active' | 'trialing' | 'canceled' | 'past_due'

// Pricing configuration
export const TIER_PRICING = {
  professional: { monthly: 20, annual: 200 },
} as const

// AI Token limits per tier
export const AI_TOKEN_LIMITS = {
  professional: 5_000_000,  // 5M tokens per month
} as const

// Trial configuration
export const TRIAL_CONFIG = {
  durationDays: 14,
  tier: 'professional' as SubscriptionTier,
  aiRequestLimit: 50,
} as const

// Token pack pricing
export const TOKEN_PACKS = {
  small: { tokens: 500_000, priceCents: 1000, label: '500K' },
  medium: { tokens: 1_500_000, priceCents: 2500, label: '1.5M' },
  large: { tokens: 5_000_000, priceCents: 7500, label: '5M' },
} as const

export type TokenPackType = keyof typeof TOKEN_PACKS

// Default spending cap in cents ($50)
export const DEFAULT_SPENDING_CAP_CENTS = 5000

export const TIER_FEATURES = {
  professional: {
    name: 'Professional',
    price: 20,
    annualPrice: 200,
    description: 'Everything you need to find, track, and win government contracts',
    aiTokens: 5_000_000,
    features: [
      'Full dashboard access & AI-matched opportunities',
      'Access to all SLED and Federal Opportunities Nationwide',
      'Bid Management & Tracking',
      'Automated email notifications',
      'AI-powered chatbot support',
      'AI Proposal Generator — create winning proposals in minutes',
      'Full Knowledge Base — upload & index company documents',
      'Advanced Analytics & Reporting',
      'AI-Powered Opportunity Insights',
      '5M AI tokens per month',
      'Priority Support',
    ]
  },
}

export function hasAccess(userTier: string, requiredTier: string): boolean {
  if (requiredTier === 'professional') {
    return userTier === 'professional'
  }
  // free-tier required means everyone has access
  return true
}

export function getFeatures(tier: SubscriptionTier): string[] {
  return TIER_FEATURES[tier as keyof typeof TIER_FEATURES]?.features || []
}

export function getTierPrice(tier: SubscriptionTier, interval: BillingInterval = 'month'): number {
  const tierConfig = TIER_FEATURES[tier as keyof typeof TIER_FEATURES]
  if (!tierConfig) return 0
  return interval === 'year' ? (tierConfig.annualPrice || 0) : (tierConfig.price || 0)
}

export function getAiTokenLimit(tier: SubscriptionTier): number {
  return AI_TOKEN_LIMITS[tier as keyof typeof AI_TOKEN_LIMITS] || 0
}

export function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(0)}K`
  }
  return tokens.toString()
}

export function hasAiAccess(tier: SubscriptionTier): boolean {
  return tier === 'professional'
}
